'use client';

import { Calendar, Tag, ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import type { BlogPost } from '@/lib/blog-data';

interface BlogPostProps {
  post: BlogPost;
}

export default function BlogPost({ post }: BlogPostProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 渲染Markdown内容，将图片替换为普通图片元素
  const renderContent = (content: string) => {
    const parts = content.split(/(!\[[^\]]*\]\([^)]+\))/g);
    
    return parts.map((part, index) => {
      const imageMatch = part.match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (imageMatch) {
        const [, alt, src] = imageMatch;
        return (
          <div key={index} className="my-6">
            <img
              src={src}
              alt={alt}
              width={800}
              height={600}
              className="w-full h-auto rounded-lg"
              loading="lazy"
            />
          </div>
        );
      }
      
      // 渲染其他Markdown内容
      const processedText = part
        .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-slate-300 pl-4 py-2 bg-slate-50 text-slate-700 italic my-4">$1</blockquote>')
        .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold text-slate-900 mt-8 mb-4">$1</h2>')
        .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-slate-900 mt-6 mb-3">$1</h3>')
        .replace(/^\- (.+)$/gm, '<li class="ml-4 mb-2">$1</li>')
        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
        .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
        .replace(/`(.+?)`/g, '<code class="bg-slate-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-slate-700 hover:text-slate-900 underline" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/\n\n/g, '</p><p class="mb-4">')
        .replace(/^(.+)$/gm, '<p class="mb-4">$1</p>');
      
      return (
        <div 
          key={index}
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: processedText }}
        />
      );
    });
  };

  return (
    <article className="max-w-4xl mx-auto">
      {/* 返回链接 */}
      <div className="mb-6">
        <Link
          href="/blog"
          className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Link>
      </div>

      {/* 文章头部 */}
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
          {post.title}
        </h1>
        
        <div className="flex items-center gap-4 text-slate-600 mb-6">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(post.date)}</span>
          </div>
        </div>


        {/* 标签 */}
        <div className="flex flex-wrap gap-2">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full"
            >
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      </header>

      {/* 文章内容 */}
      <div className="prose prose-lg max-w-none">
        {renderContent(post.content)}
      </div>

      {/* 文章底部 */}
      <footer className="mt-12 pt-8 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <div className="text-slate-600">
            <p className="text-sm">Published on {formatDate(post.date)}</p>
          </div>
          <Link
            href="/blog"
            className="inline-flex items-center text-slate-700 hover:text-slate-900 font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
        </div>
      </footer>
    </article>
  );
}
